"use client"

import Image from "next/image"
import { useState } from "react"
import { ArrowRight, CheckCircle2, ClipboardList, LineChart, ShieldCheck } from "lucide-react"

const cases = [
  {
    key: "positioning",
    number: "01",
    sector: "Advisory firm / Positioning",
    question: "How do we make the offer easier to understand without making the firm sound generic?",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1400&q=90",
    alt: "Consultants and executives reviewing a business strategy on a conference table",
    before: "The firm had strong referrals, but new buyers needed too much explanation before they understood the value.",
    artifact: "Offer architecture + buyer language map",
    after: "Leadership aligned around clearer categories, sharper proof, and a simpler path from first conversation to proposal.",
    signal: "Market clarity",
    metrics: ["Client interviews", "Offer map", "Proof library"],
    icon: LineChart,
  },
  {
    key: "delivery-model",
    number: "02",
    sector: "Professional services / Delivery model",
    question: "Why does quality depend on the same senior people catching every exception?",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=90",
    alt: "Executive consulting workshop with leaders discussing an operating model",
    before: "Client work was trusted, but the delivery system relied on undocumented judgment and constant escalation.",
    artifact: "Delivery standards + escalation treaty",
    after: "The team separated premium judgment from repeatable process and made exceptions visible before they became emergencies.",
    signal: "Delivery confidence",
    metrics: ["Role clarity", "Decision gates", "Client rhythm"],
    icon: ClipboardList,
  },
  {
    key: "growth-narrative",
    number: "03",
    sector: "Founder-led services / Growth narrative",
    question: "How do we explain growth priorities so the team can choose what not to do?",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=90",
    alt: "Business analysis documents prepared for an executive strategy discussion",
    before: "The plan was ambitious, but every function translated the priorities into a different operating reality.",
    artifact: "Growth narrative with operating thresholds",
    after: "The conversation moved from activity to tradeoffs, owners, and the evidence that would change the next move.",
    signal: "Strategic focus",
    metrics: ["Priority logic", "Tradeoff map", "Owner cadence"],
    icon: ShieldCheck,
  },
]

export function DecisionCaseRoom() {
  const [activeKey, setActiveKey] = useState(cases[0].key)
  const activeCase = cases.find((caseFile) => caseFile.key === activeKey) ?? cases[0]
  const ActiveIcon = activeCase.icon

  return (
    <section id="receipts" className="case-room relative overflow-hidden bg-muted px-5 py-24 text-foreground sm:px-8 lg:py-32">
      <div className="absolute inset-x-0 top-0 h-3 bg-[repeating-linear-gradient(90deg,hsl(var(--primary))_0_8rem,hsl(var(--accent))_8rem_13rem,hsl(var(--primary-glow))_13rem_17rem,hsl(var(--secondary))_17rem_23rem)]" />
      <div className="absolute inset-0 opacity-[0.32] [background-image:linear-gradient(rgba(16,17,15,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(16,17,15,0.08)_1px,transparent_1px)] [background-size:54px_54px]" />

      <div className="relative mx-auto w-full max-w-[94rem]">
        <div className="grid gap-8 lg:grid-cols-[1fr_0.48fr] lg:items-end">
          <div>
            <p className="font-mono text-xs font-black uppercase tracking-[0.24em] text-accent">Case room</p>
            <h2 className="mt-5 max-w-6xl font-serif text-5xl font-black leading-none sm:text-7xl lg:text-8xl">
              Proof should look like better decisions becoming routine.
            </h2>
          </div>
          <p className="max-w-xl text-lg leading-8 text-muted-foreground">
            Placeholder case files for a professional services brand: the client context, the practical artifact, and the behavior the work is designed to change.
          </p>
        </div>

        <div className="case-room-shell mt-14 grid gap-5 lg:grid-cols-[24rem_minmax(0,1fr)]">
          <div className="case-docket grid gap-3 lg:content-start">
            {cases.map((caseFile) => (
              <button
                key={caseFile.key}
                type="button"
                aria-pressed={activeKey === caseFile.key}
                data-active={activeKey === caseFile.key ? "true" : "false"}
                onClick={() => setActiveKey(caseFile.key)}
                className="case-tab group border border-primary/15 bg-card/80 p-5 text-left"
              >
                <span className="flex items-center justify-between gap-4">
                  <span className="font-mono text-xs font-black uppercase tracking-[0.22em] text-accent">
                    Case {caseFile.number}
                  </span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </span>
                <span className="mt-5 block font-serif text-2xl font-black leading-none">{caseFile.sector}</span>
                <span className="mt-4 block text-sm leading-6 text-muted-foreground">{caseFile.signal}</span>
              </button>
            ))}
          </div>

          <article key={activeCase.key} className="case-file grid overflow-hidden bg-primary text-primary-foreground lg:grid-cols-[0.92fr_1.08fr]">
            <div className="relative min-h-[21rem] overflow-hidden lg:min-h-[37rem]">
              <Image
                key={activeCase.image}
                src={activeCase.image}
                alt={activeCase.alt}
                fill
                sizes="(min-width: 1024px) 38vw, 100vw"
                className="case-room-image object-cover"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,17,15,0.02),rgba(16,17,15,0.82))]" />
              <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-primary/75 px-4 py-2 font-mono text-[0.62rem] font-black uppercase tracking-[0.18em] text-accent backdrop-blur">
                <CheckCircle2 className="size-4" />
                Placeholder client room
              </div>
              <div className="absolute inset-x-5 bottom-5 border-l-4 border-accent bg-primary/75 p-5 backdrop-blur">
                <p className="font-mono text-[0.64rem] font-black uppercase tracking-[0.22em] text-white/50">
                  Signal captured
                </p>
                <p className="mt-2 font-serif text-3xl font-black leading-none">{activeCase.signal}</p>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-center justify-between gap-5">
                <p className="font-mono text-xs font-black uppercase tracking-[0.22em] text-accent">
                  Case file / {activeCase.number}
                </p>
                <div className="grid size-12 place-items-center rounded-full bg-accent text-accent-foreground">
                  <ActiveIcon className="size-5" />
                </div>
              </div>

              <h3 className="mt-7 font-serif text-3xl font-black leading-none sm:text-4xl lg:text-5xl">
                {activeCase.question}
              </h3>

              <div className="mt-8 border-y border-white/16">
                <div className="case-evidence-lane grid gap-3 border-b border-white/12 py-5 sm:grid-cols-[9rem_1fr]">
                  <p className="font-mono text-[0.66rem] font-black uppercase tracking-[0.2em] text-white/42">Before</p>
                  <p className="text-lg leading-8 text-white/72">{activeCase.before}</p>
                </div>
                <div className="case-evidence-lane grid gap-3 border-b border-white/12 py-5 sm:grid-cols-[9rem_1fr]">
                  <p className="font-mono text-[0.66rem] font-black uppercase tracking-[0.2em] text-secondary">Artifact</p>
                  <p className="font-serif text-3xl font-black leading-none text-white">{activeCase.artifact}</p>
                </div>
                <div className="case-evidence-lane grid gap-3 py-5 sm:grid-cols-[9rem_1fr]">
                  <p className="font-mono text-[0.66rem] font-black uppercase tracking-[0.2em] text-accent">Changed</p>
                  <p className="text-lg font-semibold leading-8 text-white">{activeCase.after}</p>
                </div>
              </div>

              <div className="mt-8 grid gap-px overflow-hidden bg-white/16 sm:grid-cols-3">
                {activeCase.metrics.map((metric) => (
                  <div key={metric} className="case-metric bg-primary/80 p-5">
                    <p className="font-mono text-[0.68rem] font-black uppercase tracking-[0.18em] text-white/48">Evidence</p>
                    <p className="mt-3 font-serif text-2xl font-black leading-none text-accent">{metric}</p>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  )
}
